const slug =
	<TFn extends (...args: any[]) => string>(func: TFn) =>
	(...args: Parameters<TFn>) => {
		const modifiedArgs = args.map((arg) => encodeURIComponent(arg));
		return func(...modifiedArgs);
	};

const pret = (val: string) => val.toLowerCase().replace(/[^a-zA-Z0-9]/g, "-");

export const routes = {
	contact: "/contact",
	franchise: "/franchise",
	franchiseV2: "/franchise-v2",
	thumbnail: "/thumbnail/[hashed]",
	accountName: {
		maps: {
			root: "/[account-name]/maps",
			mapId: {
				mapName: {
					root: "/[account-name]/maps/[map-id]/[map-name]",
					layers: {
						root: "/[account-name]/maps/[map-id]/[map-name]/layers",
						layerId: {
							details:
								"/[account-name]/maps/[map-id]/[map-name]/layers/[layer-id]/details",
							style:
								"/[account-name]/maps/[map-id]/[map-name]/layers/[layer-id]/style",
							label:
								"/[account-name]/maps/[map-id]/[map-name]/layers/[layer-id]/label",
							popup:
								"/[account-name]/maps/[map-id]/[map-name]/layers/[layer-id]/popup",
							attributeFormatting:
								"/[account-name]/maps/[map-id]/[map-name]/layers/[layer-id]/attribute-formatting",
						},
					},
					tools: "/[account-name]/maps/[map-id]/[map-name]/tools",
					access: "/[account-name]/maps/[map-id]/[map-name]/access",
					settings: {
						root: "/[account-name]/maps/[map-id]/[map-name]/settings",
						navigation:
							"/[account-name]/maps/[map-id]/[map-name]/settings/navigation",
					},
				},
			},
		},
		data: {
			root: "/[account-name]/data",
			datasetId: {
				root: "/[account-name]/data/[dataset-id]",
				settings: "/[account-name]/data/[dataset-id]/settings",
				update: "/[account-name]/data/[dataset-id]/update",
				sync: "/[account-name]/data/[dataset-id]/sync",
				access: "/[account-name]/data/[dataset-id]/access",
			},
		},
		users: {
			root: "/[account-name]/users",
		},
		groups: {
			root: "/[account-name]/groups",
		},
		branding: {
			root: "/[account-name]/branding",
			localisation: "/[account-name]/branding/localisation",
			domainAndWhiteLabel: "/[account-name]/branding/domain-and-white-label",
		},
		support: {
			root: "/[account-name]/support",
		},
		settings: {
			root: "/[account-name]/settings",
			profile: "/[account-name]/settings/profile",
			password: "/[account-name]/settings/password",
			checkout: "/[account-name]/settings/checkout",
			cancelSubscription: "/[account-name]/settings/cancel-subscription",
			invoice: "/[account-name]/settings/invoice",
			editSubscription: "/[account-name]/settings/edit-subscription",
			subscriptionConfirmation:
				"/[account-name]/settings/subscription-confirmation",
		},
	},
};

export const paths = {
	unauthorized: "/401",
	notFound: "/404",
	sererError: "/500",
	pricing: "/pricing",
	home: "/",
	privacy: "/privacy",
	terms: "/terms",
	signUp: "/sign-up",
	signUpV2: "/sign-up-v2",
	signUpVerification: "/sign-up-verification",
	signUpVerificationV2: slug(
		(email) => `/sign-up-verification-v2?email=${email}`,
	),
	forgotPassword: "/forgot-password",
	forgotPasswordV2: "/forgot-password-v2",
	updatePasswordV2: slug((email) => `/update-password-v2?email=${email}`),
	signIn: "/sign-in",
	signInV2: "/sign-in-v2",
	accountName: {
		signIn: slug((accountName) => `/${accountName}/sign-in`),
		maps: {
			root: slug((accountName) => `/${accountName}/maps`),
			new: slug(
				(accountName) => `/${accountName}/maps/new-map-id/new-map-name`,
			),
			mapId: {
				mapName: {
					root: slug(
						(accountName, mapId, mapName) =>
							`/${accountName}/maps/${mapId}/${pret(mapName)}`,
					),
					layers: {
						root: slug(
							(accountName, mapId, mapName) =>
								`/${accountName}/maps/${mapId}/${pret(mapName)}/layers`,
						),
						layerId: {
							details: slug(
								(accountName, mapId, mapName, layerId) =>
									`/${accountName}/maps/${mapId}/${pret(
										mapName,
									)}/layers/${layerId}/details`,
							),
							style: slug(
								(accountName, mapId, mapName, layerId) =>
									`/${accountName}/maps/${mapId}/${pret(
										mapName,
									)}/layers/${layerId}/style`,
							),
							label: slug(
								(accountName, mapId, mapName, layerId) =>
									`/${accountName}/maps/${mapId}/${pret(
										mapName,
									)}/layers/${layerId}/label`,
							),
							popup: slug(
								(accountName, mapId, mapName, layerId) =>
									`/${accountName}/maps/${mapId}/${pret(
										mapName,
									)}/layers/${layerId}/popup`,
							),
							attributeFormatting: slug(
								(accountName, mapId, mapName, layerId) =>
									`/${accountName}/maps/${mapId}/${pret(
										mapName,
									)}/layers/${layerId}/attribute-formatting`,
							),
						},
					},
					addLayer: slug(
						(accountName, mapId, mapName, layerGroupId) =>
							`/${accountName}/maps/${mapId}/${pret(
								mapName,
							)}/add-layer?layer-group-id=${layerGroupId}`,
					),
					tools: {
						root: slug(
							(accountName, mapId, mapName) =>
								`/${accountName}/maps/${mapId}/${pret(mapName)}/tools`,
						),
						legend: slug(
							(accountName, mapId, mapName) =>
								`/${accountName}/maps/${mapId}/${pret(mapName)}/tools/legend`,
						),
						search: slug(
							(accountName, mapId, mapName) =>
								`/${accountName}/maps/${mapId}/${pret(mapName)}/tools/search`,
						),
						advancedSearch: slug(
							(accountName, mapId, mapName) =>
								`/${accountName}/maps/${mapId}/${pret(
									mapName,
								)}/tools/advanced-search`,
						),
						query: slug(
							(accountName, mapId, mapName) =>
								`/${accountName}/maps/${mapId}/${pret(mapName)}/tools/query`,
						),
						proximityAnalysis: slug(
							(accountName, mapId, mapName) =>
								`/${accountName}/maps/${mapId}/${pret(
									mapName,
								)}/tools/proximity-analysis`,
						),
						informationSidebar: slug(
							(accountName, mapId, mapName) =>
								`/${accountName}/maps/${mapId}/${pret(
									mapName,
								)}/tools/information-sidebar`,
						),
						print: slug(
							(accountName, mapId, mapName) =>
								`/${accountName}/maps/${mapId}/${pret(mapName)}/tools/print`,
						),
						measure: slug(
							(accountName, mapId, mapName) =>
								`/${accountName}/maps/${mapId}/${pret(mapName)}/tools/measure`,
						),
						copyright: slug(
							(accountName, mapId, mapName) =>
								`/${accountName}/maps/${mapId}/${pret(
									mapName,
								)}/tools/copyright`,
						),
						mouseHighlight: slug(
							(accountName, mapId, mapName) =>
								`/${accountName}/maps/${mapId}/${pret(
									mapName,
								)}/tools/mouse-highlight`,
						),
					},
					access: slug(
						(accountName, mapId, mapName) =>
							`/${accountName}/maps/${mapId}/${pret(mapName)}/access`,
					),
					settings: {
						root: slug(
							(accountName, mapId, mapName) =>
								`/${accountName}/maps/${mapId}/${pret(mapName)}/settings`,
						),
						navigation: slug(
							(accountName, mapId, mapName) =>
								`/${accountName}/maps/${mapId}/${pret(
									mapName,
								)}/settings/navigation`,
						),
					},
				},
			},
			printPreview: slug(
				(accountName, mapId) =>
					`/${accountName}/maps/${mapId}?print-preview=true`,
			),
		},
		data: {
			root: slug((accountName) => `/${accountName}/data`),
			import: slug((accountName, layerGroupId?: string) =>
				`/${accountName}/data/import`.concat(
					layerGroupId ? `?layer-group-id=${layerGroupId}` : "",
				),
			),
			datasetId: {
				root: slug(
					(accountName, datasetId) => `/${accountName}/data/${datasetId}`,
				),
				access: slug(
					(accountName, datasetId) =>
						`/${accountName}/data/${datasetId}/access`,
				),
				sync: slug(
					(accountName, datasetId) => `/${accountName}/data/${datasetId}/sync`,
				),
				settings: slug(
					(accountName, datasetId) =>
						`/${accountName}/data/${datasetId}/settings`,
				),
				update: slug(
					(accountName, datasetId) =>
						`/${accountName}/data/${datasetId}/update`,
				),
			},
		},
		users: {
			root: slug((accountName) => `/${accountName}/users`),
			invite: slug((accountName) => `/${accountName}/users/invite`),
			edit: slug(
				(accountName, userId) => `/${accountName}/users/${userId}/edit`,
			),
			join: slug(
				(accountName, userId) => `/${accountName}/users/${userId}/join`,
			),
		},
		groups: {
			root: slug((accountName) => `/${accountName}/groups`),
			create: slug((accountName) => `/${accountName}/groups/create`),
			edit: slug(
				(accountName, groupId) => `/${accountName}/groups/${groupId}/edit`,
			),
		},
		branding: {
			root: slug((accountName) => `/${accountName}/branding`),
			portal: slug((accountName) => `/${accountName}/branding/portal`),
			theme: slug((accountName) => `/${accountName}/branding/theme`),
			localisation: slug(
				(accountName) => `/${accountName}/branding/localisation`,
			),
			domainAndWhiteLabel: slug(
				(accountName) => `/${accountName}/branding/domain-and-white-label`,
			),
			analytics: slug((accountName) => `/${accountName}/branding/analytics`),
		},
		support: slug((accountName) => `/${accountName}/support`),
		settings: {
			root: slug((accountName) => `/${accountName}/settings`),
			checkout: slug((accountName) => `/${accountName}/settings/checkout`),
			cancelSubscription: slug(
				(accountName) => `/${accountName}/settings/cancel-subscription`,
			),
			editSubscription: slug(
				(accountName) => `/${accountName}/settings/edit-subscription`,
			),
			subscriptionConfirmation: slug(
				(accountName) => `/${accountName}/settings/subscription-confirmation`,
			),
			invoice: slug((accountName) => `/${accountName}/settings/invoice`),
			profile: slug((accountName) => `/${accountName}/settings/profile`),
			password: slug((accountName) => `/${accountName}/settings/password`),
		},
	},
} as const;

const isDev = process.env.SST_STAGE === "dev";
export const signInV1Or2 = isDev ? paths.signInV2 : paths.signIn;
export const signUpV1Or2 = isDev ? paths.signUpV2 : paths.signUp;
export const franchiseV1Or2 = isDev ? routes.franchiseV2 : routes.franchise;
export const contactV1Or2 = isDev
	? routes.contact
	: "mailto:sales@mangomap.com";
